import { useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { uploadImage } from "@/lib/storage";
import { useToast } from "@/context/ToastContext";

const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/avif"];
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

export default function ImageUploader({
  value,
  onChange,
  folder = "general",
  aspect = "aspect-[16/10]",
  label = "Imagen",
}: {
  value: string;
  onChange: (url: string) => void;
  folder?: string;
  aspect?: string;
  label?: string;
}) {
  const { error: toastError } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toastError("Formato no permitido. Usa JPG, PNG, WEBP o AVIF.");
      return;
    }
    if (file.size > MAX_SIZE) {
      toastError("La imagen supera los 10 MB.");
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadImage(file, folder);
      onChange(url);
    } catch {
      toastError("No se pudo subir la imagen.");
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    handleFile(e.target.files?.[0]);
    e.target.value = "";
  }

  return (
    <div>
      <span className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</span>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative w-full ${aspect} rounded-lg border-2 border-dashed overflow-hidden cursor-pointer transition-colors ${
          dragging ? "border-primary-400 bg-primary-50" : "border-background-300 bg-background-50"
        }`}
      >
        {value ? (
          <img src={value} alt="Vista previa" className="w-full h-full object-cover object-top" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-foreground-400">
            <i className="ri-image-add-line text-3xl" />
            <p className="text-xs">Arrastra una imagen o haz clic para subir</p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-background-950/50 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          </div>
        )}

        {value && !uploading && (
          <div className="absolute bottom-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-950/70 text-white hover:bg-red-600 transition-colors"
              aria-label="Quitar imagen"
            >
              <i className="ri-delete-bin-line" />
            </button>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(",")}
        onChange={onInputChange}
        className="hidden"
      />
    </div>
  );
}